import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Save,
    Building2,
    User,
    Mail,
    Phone,
    Check,
    Loader2,
    AlertTriangle,
} from 'lucide-react';
import useAuthStore, { PERMISSIONS } from '../stores/authStore';
import api from '../api/client';

export default function CafeEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = useAuthStore();

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);
    // Read-only context loaded from the backend (owner details).
    const [detail, setDetail] = useState(null);
    // Editable fields — only these are real Cafe columns the backend persists.
    const [formData, setFormData] = useState({ name: '', location: '', city: '', phone: '' });

    const canEdit = hasPermission(PERMISSIONS.EDIT_CAFE_DETAILS) || isSuperAdmin();

    const load = useCallback(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        api.get(`/internal/cafes/${id}`)
            .then((res) => {
                if (cancelled) return;
                const d = res.data;
                setDetail(d);
                setFormData({
                    name: d.name || '',
                    location: d.location || '',
                    city: d.city || '',
                    phone: d.phone || '',
                });
            })
            .catch((err) => {
                console.error('Failed to load café:', err);
                if (!cancelled) setError(err.response?.data?.detail || 'Failed to load café');
            })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
    }, [id]);

    useEffect(() => load(), [load]);

    const handleChange = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);
        try {
            await api.put(`/internal/cafes/${id}`, {
                name: formData.name,
                location: formData.location,
                city: formData.city,
                phone: formData.phone,
            });
            setSaved(true);
            setTimeout(() => navigate(`/cafes/${id}`), 1000);
        } catch (err) {
            console.error('Failed to save café:', err);
            setError(err.response?.data?.detail || 'Failed to save changes');
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="loading-state">
                <div className="spinner" />
                <style>{`.loading-state { display: flex; align-items: center; justify-content: center; min-height: 50vh; }`}</style>
            </div>
        );
    }

    if (error && !detail) {
        return (
            <div className="empty-state">
                <Building2 size={48} />
                <p>{error}</p>
                <button className="btn btn--secondary" onClick={() => navigate('/cafes')}>Back to Cafés</button>
            </div>
        );
    }

    if (saved) {
        return (
            <div className="success-state">
                <div className="success-icon"><Check size={32} /></div>
                <h2>Changes Saved</h2>
                <p>Redirecting...</p>
                <style>{`
          .success-state { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 50vh; text-align: center; }
          .success-icon { width: 64px; height: 64px; background: var(--status-success-subtle); color: var(--status-success); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: var(--space-6); }
          .success-state h2 { font-size: var(--text-xl); margin-bottom: var(--space-2); }
          .success-state p { color: var(--text-tertiary); }
        `}</style>
            </div>
        );
    }

    return (
        <div className="cafe-edit">
            <button onClick={() => navigate(`/cafes/${id}`)} className="back-link">
                <ArrowLeft size={16} />
                <span>Back to Café</span>
            </button>

            <div className="page-header">
                <h1 className="page-title">Edit Café</h1>
                <p className="page-subtitle">{detail?.name}</p>
            </div>

            {error && <div className="form-error"><AlertTriangle size={16} /> {error}</div>}

            <form onSubmit={handleSubmit} className="edit-form">
                <section className="form-section glass-card">
                    <h2 className="section-title"><Building2 size={18} /> Basic Information</h2>
                    <div className="form-grid">
                        <div className="input-group">
                            <label className="input-label">Café Name</label>
                            <input type="text" className="input" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} disabled={!canEdit} required />
                        </div>
                        <div className="input-group">
                            <label className="input-label">Location</label>
                            <input type="text" className="input" value={formData.location} onChange={(e) => handleChange('location', e.target.value)} disabled={!canEdit} />
                        </div>
                        <div className="input-group">
                            <label className="input-label">City</label>
                            <input type="text" className="input" value={formData.city} onChange={(e) => handleChange('city', e.target.value)} disabled={!canEdit} placeholder="e.g. Hyderabad" />
                        </div>
                        <div className="input-group">
                            <label className="input-label">Café Phone</label>
                            <input type="tel" className="input" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} disabled={!canEdit} />
                        </div>
                    </div>
                </section>

                {/* Owner details live on the User record, not the Café — read-only here. */}
                <section className="form-section glass-card">
                    <h2 className="section-title"><User size={18} /> Owner Details <span className="readonly-tag">read-only</span></h2>
                    <div className="readonly-grid">
                        <div className="readonly-item"><User size={15} /><span>{detail?.owner_name || '—'}</span></div>
                        <div className="readonly-item"><Mail size={15} /><span>{detail?.owner_email || '—'}</span></div>
                        <div className="readonly-item"><Phone size={15} /><span>{detail?.owner_phone || '—'}</span></div>
                    </div>
                    <p className="section-note">Owner identity is managed on the Users page; it can't be changed from here.</p>
                </section>

                <div className="form-actions">
                    <button type="button" className="btn btn--ghost" onClick={() => navigate(`/cafes/${id}`)}>Cancel</button>
                    <button type="submit" className="btn btn--primary" disabled={isSaving || !canEdit}>
                        {isSaving ? <><Loader2 size={16} className="spin" /> Saving...</> : <><Save size={16} /> Save Changes</>}
                    </button>
                </div>
            </form>


            <style>{`
        .cafe-edit {
          /* noLag rebrand: scoped to this page only, same pattern as the
             other rebranded pages. No Figma node exists for this page —
             applied by established pattern, not verified against a design. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          max-width: 800px;
        }
        .cafe-edit .glass-card {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%);
          backdrop-filter: blur(14px) saturate(180%);
          -webkit-backdrop-filter: blur(14px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45), 0 20px 48px rgba(0, 0, 0, 0.5);
        }
        .cafe-edit .glass-card::before { display: none; }
        .back-link { display: inline-flex; align-items: center; gap: var(--space-2); color: var(--text-secondary); font-size: var(--text-sm); margin-bottom: var(--space-6); background: none; border: none; cursor: pointer; transition: color var(--duration-fast) var(--ease-out); }
        .back-link:hover { color: var(--accent-primary); }
        .page-header { margin-bottom: var(--space-8); }
        .page-title { font-size: var(--text-2xl); font-weight: 600; margin-bottom: var(--space-2); letter-spacing: -0.02em; }
        .page-subtitle { font-size: var(--text-sm); color: var(--text-tertiary); }
        .form-error { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-3) var(--space-4); margin-bottom: var(--space-5); background: var(--status-danger-subtle); color: var(--status-danger); border-radius: var(--radius-md); font-size: var(--text-sm); }
        .edit-form { display: flex; flex-direction: column; gap: var(--space-6); }
        .form-section { padding: var(--space-6); }
        .section-title { display: flex; align-items: center; gap: var(--space-3); font-size: var(--text-base); font-weight: 600; margin-bottom: var(--space-6); color: var(--text-primary); }
        .section-title svg { color: var(--text-tertiary); }
        .readonly-tag { font-size: var(--text-xs); font-weight: 500; color: var(--text-tertiary); background: var(--glass-bg); border: 1px solid var(--glass-border); border-radius: var(--radius-sm); padding: 2px 8px; }
        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--space-5); }
        .readonly-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--space-4); }
        .readonly-item { display: flex; align-items: center; gap: var(--space-2); font-size: var(--text-sm); color: var(--text-secondary); }
        .readonly-item svg { color: var(--text-tertiary); flex-shrink: 0; }
        .section-note { margin-top: var(--space-4); font-size: var(--text-xs); color: var(--text-tertiary); }
        .form-actions { display: flex; justify-content: flex-end; gap: var(--space-3); padding-top: var(--space-6); border-top: 1px solid var(--divider); }
        @media (max-width: 640px) { .form-grid, .readonly-grid { grid-template-columns: 1fr; } }
      `}</style>
        </div>
    );
}
